import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import { CardActions } from "@mui/material"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import { TT_GO_TO_DEVICE_SUMMARY } from "../../../constants/tooltips"
import { SINGLE_GEN_DEVICE_NAV_LINK } from "../../../router/routes"
import { TAttachment } from "../../../services/middleware/mappers/dnoInterface/dnoApplicationsMapper"
import AppButton from "../../buttons/appButton"
import AppTooltip from "../../helper/appTooltip"
import { AppCard, AppCardContent, AppCardHeader } from "../../surfaces/appCard"
import SingleAttachment from "../attachments/singleAttachment"

export const G100_EXPORT_LIMIT_TYPE = "G100"
export const OTHER_EXPORT_LIMIT_TYPE = "Other"

interface IExportLimitDeviceCardProps {
    capacity: string
    type: string
    sldAttachment: TAttachment
    exportLimitDeviceDescription: string | null
    g100DeviceSysRef: string | null
    applicationId: string
    disabled?: boolean
}

/**
 * Card for displaying details about the export limit device on a property.
 * Designed for use on the single application summary page. Handles three
 * different types of export limit device:
 * - G100
 * - Manufacturer
 * - Other
 */
const ExportLimitDeviceCard = ({
    capacity,
    type,
    sldAttachment,
    exportLimitDeviceDescription,
    g100DeviceSysRef,
    applicationId,
    disabled,
}: IExportLimitDeviceCardProps): ReactElement => {
    const navigate = useNavigate()
    return (
        <AppCard sx={{ px: 0 }}>
            <AppCardHeader
                title="Export Limit Device"
                subheader={`Type: ${type}`}
            />
            <AppCardContent>
                <p>
                    {`Installer has indicated there is an export limit device on site,
            which limits the export power to ${capacity} kW. Therefore, total aggregate
            generation of the application has been set to ${capacity} kW`}
                </p>
                <p>
                    {type === OTHER_EXPORT_LIMIT_TYPE &&
                        `As this device has been classed as 'Other' (and not 'G100' or 'Manufacturer'), the installer has provided the following additional details: ${exportLimitDeviceDescription}"`}
                </p>
            </AppCardContent>

            <CardActions sx={{ justifyContent: "flex-end" }}>
                <SingleAttachment
                    applicationId={applicationId}
                    filename="Export Limit Device SLD"
                    fileUrl={sldAttachment.fileUrl}
                    isDnoMode
                    disabled={disabled}
                />
                {type === G100_EXPORT_LIMIT_TYPE && (
                    <AppTooltip title={TT_GO_TO_DEVICE_SUMMARY}>
                        <AppButton
                            variant="outlined"
                            endIcon={<NavigateNextIcon />}
                            sx={{ ml: 1 }}
                            onClick={() =>
                                navigate(
                                    SINGLE_GEN_DEVICE_NAV_LINK(
                                        g100DeviceSysRef as string
                                    )
                                )
                            }
                        >
                            View Device Details
                        </AppButton>
                    </AppTooltip>
                )}
            </CardActions>
        </AppCard>
    )
}

ExportLimitDeviceCard.defaultProps = {
    disabled: false,
}

export default ExportLimitDeviceCard
